(ns hai.notes.recent
  "The user's latest notes and the in-card editor over them, shared by Home's
   My Notes card and the sidebar's notes card. The slice lives here and the
   functions patch it — a host threads them through its own events, the way
   [[hai.entity.draft]] is used. What a host does around the editor rides the
   event payloads: `:initial-note` on `::create`, and `-fx` hooks on `::save`,
   `::refresh-editing` and `::delete`, in the `:on-success-fx` style, with
   `'?result` substituted by the surface's `:resolve` effect."
  (:require
   [hai.entity.draft :as draft]
   [hai.notes.eql :as eql]
   [hai.notes.form :as form]
   [hai.notes.mutations :as mutations]
   [hai.ui.request :as request]
   [hai.ui.rf-sugar :refer [defevent defevent-fx defsub]]))

(def limit
  "How many notes the list holds — the resolver's cut, and what the footer
   under the list says."
  20)

(def draft-id
  "The draft behind the in-card editor — one note is open at a time."
  ::draft)

;;
;; The slice — db -> db, for a host's own events as much as the ones below.
;;

(defn notes [db]
  (get db ::notes []))

(defn loaded? [db]
  (contains? db ::notes))

(defn- note-by-uuid [notes note-uuid]
  (some (fn [note]
          (when (= note-uuid (:note/uuid note))
            note))
        notes))

(defn find-note [db note-uuid]
  (note-by-uuid (notes db) note-uuid))

(defn- without-note [notes note-uuid]
  (into [] (remove #(= note-uuid (:note/uuid %))) notes))

(defn insert-note
  "A just-created note goes on top and the list keeps its length. Nothing
   until the list has loaded — before that a create would stand in as a
   one-row list."
  [db note]
  (cond-> db
    (loaded? db) (update ::notes (fn [notes]
                                   (into [note]
                                         (take (dec limit))
                                         (without-note notes (:note/uuid note)))))))

(defn editing-uuid
  "The uuid of the note the editor is on — nil on a draft, or with the editor
   closed."
  [db]
  (:note/uuid (::editing db)))

(defn patch-note
  "Merges `partial-note` into its row, when the list has it, and into the
   note the editor is on, when that is the one."
  [db partial-note]
  (let [note-uuid (:note/uuid partial-note)
        merge-match (fn [note]
                      (if (= note-uuid (:note/uuid note))
                        (merge note partial-note)
                        note))]
    (cond-> db
      (loaded? db) (update ::notes (partial mapv merge-match))
      (= note-uuid (editing-uuid db)) (update ::editing merge partial-note))))

(defn remove-note [db note-uuid]
  (cond-> db
    (loaded? db) (update ::notes without-note note-uuid)))

(defn close-editor [db]
  (-> db
      (dissoc ::editing)
      (draft/discard draft-id)))

(defevent-fx ::load [{:keys [db]} _]
  (when-not (request/in-flight? db ::load)
    {:db (request/mark-in-flight db ::load)
     :resolve {:query [{(list :user/recent-notes {:limit limit}) eql/note-eql}]
               :request-id ::load
               :transform-fn (fn [resp] (vec (:user/recent-notes resp)))
               :store-in [::notes]
               :on-error-fx [[:dispatch [:toast/error {:text "Could not load notes. Try again."}]]]}}))

(defevent ::clear [db _]
  (-> db
      (dissoc ::notes)
      close-editor))

(defsub ::list [db]
  (notes db))

(defsub ::loaded? [db]
  (loaded? db))

;;
;; The in-card editor
;;

;; The editor keeps the note it is on — a row's note, or `{}` or a host's
;; initial note for a draft — rather than a uuid to re-find in the list, so a
;; host may open it on a note from any of its lists: the sidebar's page
;; groups as much as the latest notes. `patch-note` keeps it as last saved.
(defsub ::editing-note [db]
  (::editing db))

(defn open-editor
  "Opens the editor on `note` — `{}` or a host's initial note for a draft —
   with the draft seeded from it."
  [db note]
  (-> db
      (assoc ::editing note)
      (draft/seed draft-id (form/from-note note))))

(defn- editing-note
  "The note the editor is on as the server has it — nil for a draft."
  [db]
  (let [note (::editing db)]
    (when (:note/uuid note)
      note)))

(defevent ::edit [db note]
  (open-editor db note))

;; ＋ opens a blank draft — nothing exists until it is saved. A host's
;; `:initial-note` is what the draft starts with: the sidebar's carries the
;; page link.
(defevent ::create [db {:keys [initial-note]}]
  (open-editor db (or initial-note {})))

(defevent ::close-editor [db _]
  (close-editor db))

;; Cancel puts the draft back to the note as last saved — a fresh draft back
;; to what it opened with; the ✕ is what closes the editor.
(defevent ::cancel [db _]
  (draft/seed db draft-id (form/from-note (::editing db))))

(defn- note-link-action
  "The success toast's link, from a host's `:note-link` —
   `{:href-fn (fn [uuid] url) :target \"_self\" :label \"View note\"}`. The
   host owns the URL shape — a client-side route in the web app, an absolute
   platform URL in the extension — and may reword the label. nil without one,
   which leaves the toast plain text."
  [{:keys [href-fn target label]} note-uuid]
  (when href-fn
    {:label (or label "View note") :href (href-fn note-uuid) :target target}))

(defn- saved-toast [text note-link note-uuid]
  [:dispatch [:toast/success (cond-> {:text text}
                               note-uuid (assoc :action (note-link-action note-link note-uuid)))]])

;; Save keeps the editor up, its Save spinning, until the note is filed. A
;; draft's first save creates the note and closes the editor; an existing
;; note's save patches its row and stays — only the create composer is
;; one-shot. A failed save leaves the draft where it is, with a toast; an
;; existing note is re-read from the server, which is where a half-applied
;; save leaves the truth. `:on-saved-fx` runs once the note is filed here,
;; `:on-refreshed-fx` after that re-read, both with `'?result` the note.
(defevent-fx ::save
  [{:keys [db]} staged {:keys [source note-link on-saved-fx on-refreshed-fx]}]
  (when (and (::editing db) (not (request/in-flight? db ::save)))
    (let [note (editing-note db)]
      {:db (request/mark-in-flight db ::save)
       :resolve (assoc (mutations/save-resolve {:note note :source source} staged)
                       :request-id ::save
                       :on-success-fx (into [[:dispatch [::saved '?result {:created? (nil? note)
                                                                           :note-link note-link}]]]
                                            on-saved-fx)
                       :on-error-fx (cond-> [[:dispatch [:toast/error {:text "Could not save note. Try again."}]]]
                                      note (conj [:dispatch [::refresh-editing {:on-refreshed-fx on-refreshed-fx}]])))})))

(defevent-fx ::saved
  [{:keys [db]} note {:keys [created? note-link]}]
  (if created?
    {:db (-> db
             (insert-note note)
             close-editor)
     :fx [(saved-toast "Note added" note-link (:note/uuid note))]}
    {:db (patch-note db note)
     :fx [(saved-toast "Note saved" note-link (:note/uuid note))]}))

;; Re-resolve the note being edited (e.g. after the sharing modal closes, so
;; freshly-added collaborators show).
(defevent-fx ::refresh-editing
  [{:keys [db]} {:keys [on-refreshed-fx]}]
  (when-let [note-uuid (editing-uuid db)]
    {:resolve {:query [{[:note/uuid note-uuid] eql/note-eql}]
               :transform-fn (fn [resp] (get resp [:note/uuid note-uuid]))
               :on-success-fx (into [[:dispatch [::note-refreshed '?result]]] on-refreshed-fx)}}))

(defevent ::note-refreshed [db note]
  (patch-note db note))

;; Optimistic: close the editor and drop the note from the list now, with
;; `:on-deleted-fx` alongside, and `:on-delete-confirmed-fx` once the
;; platform has deleted it; an error reloads the list (the note still
;; exists) with a toast, then `:on-delete-failed-fx`.
(defevent-fx ::delete
  [{:keys [db]} {:keys [on-deleted-fx on-delete-confirmed-fx on-delete-failed-fx]}]
  (when-let [note-uuid (editing-uuid db)]
    (cond-> {:db (-> db
                     (remove-note note-uuid)
                     close-editor)
             :resolve (assoc (mutations/delete-resolve note-uuid)
                             :on-success-fx (vec on-delete-confirmed-fx)
                             :on-error-fx (into [[:dispatch [:toast/error {:text "Could not delete note. Try again."}]]
                                                 [:dispatch [::load]]]
                                                on-delete-failed-fx))}
      (seq on-deleted-fx) (assoc :fx on-deleted-fx))))
